import { Injectable, OnDestroy } from '@angular/core';
import { interpret, InterpreterFrom, StateFrom } from 'xstate';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { patientFormsMachine } from '../state-machine/patient-forms.machine';
import { Patient, PatientFormsEvent } from '../state-machine/patient-forms.types';
import { guards } from '../state-machine/patient-forms.guards';
import { actions } from '../state-machine/patient-forms.actions';
import { PatientFormsServicesToken } from './patient-forms-services.token';

export type PatientFormsMachineState = StateFrom<typeof patientFormsMachine>;

@Injectable()
export class PatientFormsMachineService implements OnDestroy {
	private service: InterpreterFrom<typeof patientFormsMachine>;
	private stateSubject!: BehaviorSubject<PatientFormsMachineState>;
	private readySubject = new BehaviorSubject<boolean>(false);
	private rxSavedSubject = new Subject<void>();
	/**
	 * Preview patient data shown in the card while the API loads the full patient link status.
	 * Populated from the Rx store (available synchronously) to avoid an empty card flash.
	 * Once markReady() is called, the machine's own context takes over.
	 */
	private previewSubject = new BehaviorSubject<Patient | null>(null);
	state$!: ReturnType<BehaviorSubject<PatientFormsMachineState>['asObservable']>;
	ready$: Observable<boolean> = this.readySubject.asObservable();
	rxSaved$: Observable<void> = this.rxSavedSubject.asObservable();
	preview$: Observable<Patient | null> = this.previewSubject.asObservable();

	set previewPatient(p: Patient | null) {
		this.previewSubject.next(p);
	}

	get state(): PatientFormsMachineState {
		return this.stateSubject.value;
	}

	constructor(private readonly machineServices: PatientFormsServicesToken) {
		const machine = patientFormsMachine.withConfig({
			guards: guards as any,
			actions: actions as any,
			services: {
				searchForMatchingPatients: (ctx, evt) => this.machineServices.searchForMatchingPatients(ctx, evt),
				checkCrossSystemMatches: (ctx, evt) => this.machineServices.checkCrossSystemMatches(ctx, evt),
				searchPatientsInDB: (ctx, evt) => this.machineServices.searchPatientsInDB(ctx, evt),
				checkDuplicateIteroPatient: (ctx, evt) => this.machineServices.checkDuplicateIteroPatient(ctx, evt)
			}
		});

		this.service = interpret(machine);
		this.stateSubject = new BehaviorSubject<PatientFormsMachineState>(this.service.getSnapshot());
		this.state$ = this.stateSubject.asObservable();

		this.service.onTransition(state => {
			this.stateSubject.next(state);
		});

		this.service.start();
	}

	ngOnDestroy(): void {
		this.service.stop();
		this.stateSubject.complete();
	}

	send(event: PatientFormsEvent): void {
		this.service.send(event);
	}

	setReadOnly(isReadOnly: boolean): void {
		this.send({ type: isReadOnly ? 'CARD.SET_READ_ONLY' : 'CARD.SET_EDITABLE' });
	}

	clear(): void {
		this.send({ type: 'CARD.CLEAR_CLICKED' });
		this.readySubject.next(false);
		this.previewSubject.next(null);
	}

	setExistingPatient(patient: Patient): void {
		this.send({ type: 'EXISTING.PATIENT_LOADED', patient });
	}

	setDefaultToggle(on: boolean): void {
		this.send({ type: 'CARD.SET_DEFAULT_TOGGLE', on });
	}

	markReady(): void {
		this.readySubject.next(true);
	}

	markRxSaved(): void {
		this.rxSavedSubject.next();
	}
}
